import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const NotarizationSuccess = ({ 
  isVisible, 
  notarizationData, 
  onClose, 
  onViewHistory 
}) => {
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = async (text, itemType) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedItem(itemType);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatTransactionHash = (hash) => {
    return `${hash?.slice(0, 10)}...${hash?.slice(-8)}`;
  };

  const openPolygonScan = () => {
    window.open(`https://polygonscan.com/tx/${notarizationData?.transactionHash}`, '_blank');
  };

  const openIPFSGateway = () => {
    window.open(notarizationData?.ipfsUrl, '_blank');
  };

  if (!isVisible || !notarizationData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-full">
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Notarization Complete!</h3>
              <p className="text-sm text-muted-foreground">
                Your webpage has been successfully archived
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Screenshot Preview */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Screenshot Preview</h4>
            <div className="relative bg-muted/30 rounded-lg overflow-hidden border">
              <Image
                src={notarizationData?.screenshotUrl}
                alt="Webpage screenshot"
                className="w-full h-48 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              <button
                onClick={openIPFSGateway}
                className="absolute top-3 right-3 flex items-center space-x-1 px-2 py-1 bg-black/50 text-white text-xs rounded-md hover:bg-black/70 transition-smooth"
              >
                <Icon name="ExternalLink" size={12} />
                <span>View Full</span>
              </button>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Notarization Details</h4>
            
            {/* URL */}
            <div className="p-3 bg-muted/30 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Original URL</span>
                <button
                  onClick={() => copyToClipboard(notarizationData?.originalUrl, 'url')}
                  className="flex items-center space-x-1 text-xs text-primary hover:text-primary/80"
                >
                  <Icon name={copiedItem === 'url' ? 'Check' : 'Copy'} size={12} />
                  <span>{copiedItem === 'url' ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <p className="text-sm text-foreground font-mono break-all">
                {notarizationData?.originalUrl}
              </p>
            </div>

            {/* IPFS Hash */}
            <div className="p-3 bg-muted/30 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">IPFS Hash</span>
                <button
                  onClick={() => copyToClipboard(notarizationData?.ipfsHash, 'ipfs')}
                  className="flex items-center space-x-1 text-xs text-primary hover:text-primary/80"
                >
                  <Icon name={copiedItem === 'ipfs' ? 'Check' : 'Copy'} size={12} />
                  <span>{copiedItem === 'ipfs' ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <p className="text-sm text-foreground font-mono break-all">
                {notarizationData?.ipfsHash}
              </p>
            </div>

            {/* Transaction Hash */}
            <div className="p-3 bg-muted/30 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Transaction Hash</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => copyToClipboard(notarizationData?.transactionHash, 'tx')}
                    className="flex items-center space-x-1 text-xs text-primary hover:text-primary/80"
                  >
                    <Icon name={copiedItem === 'tx' ? 'Check' : 'Copy'} size={12} />
                    <span>{copiedItem === 'tx' ? 'Copied!' : 'Copy'}</span>
                  </button>
                  <button
                    onClick={openPolygonScan}
                    className="flex items-center space-x-1 text-xs text-primary hover:text-primary/80"
                  >
                    <Icon name="ExternalLink" size={12} />
                    <span>View</span>
                  </button>
                </div>
              </div>
              <p className="text-sm text-foreground font-mono">
                {formatTransactionHash(notarizationData?.transactionHash)}
              </p>
            </div>

            {/* Timestamp */}
            <div className="p-3 bg-muted/30 rounded-md">
              <span className="text-xs text-muted-foreground block mb-2">Timestamp</span>
              <p className="text-sm text-foreground">
                {new Date(notarizationData.timestamp)?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={openIPFSGateway}
              iconName="Database"
              iconPosition="left"
              className="justify-center"
            >
              View on IPFS
            </Button>
            <Button
              variant="outline"
              onClick={openPolygonScan}
              iconName="ExternalLink"
              iconPosition="left"
              className="justify-center"
            >
              View on PolygonScan
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onViewHistory}
            iconName="History"
            iconPosition="left"
            className="flex-1"
          >
            View History
          </Button>
          <Button
            variant="default"
            onClick={onClose}
            className="flex-1"
          >
            Notarize Another
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotarizationSuccess;